import { React, useEffect, useState, lazy, Suspense } from 'react';
import { devLog } from './utils/devLog';
import { EncryptionKeyProvider } from './utils/encryptionKeyContext';
import { BrowserRouter as Router, Navigate, Routes, Route, useLocation, useParams } from 'react-router-dom';
import './App.css';

import { SpeedInsights } from "@vercel/speed-insights/react"
import * as encryptionUtils from './utils/encryptionUtils';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Toaster } from 'react-hot-toast';
import { FullScreenRouteSpinner } from './components/common/LoadingSpinner';
import { ActiveWorkspaceProvider } from './contexts/ActiveWorkspaceContext';
import { ClientSearchModeProvider } from './contexts/ClientSearchModeContext';
// Ensure Firebase is initialized before lazy-loaded components try to use it
import './firebase/config';
import { isCustomDomainHost, isMascotCustomHostname } from './utils/customDomainHost';
import DocPathRedirect from './components/DocPathRedirect';

// Lazy load route components for code splitting
const KnowledgeBaseUserView = lazy(() => import('./components/user/KnowledgeBaseUserView'));
const AuthenticatedHome = lazy(() => import('./components/user/AuthenticatedHome'));
const ClientSearchRouteGuard = lazy(() => import('./components/user/ClientSearchRouteGuard'));
const LandingPage = lazy(() => import('./components/user/LandingPage'));
const ProfilePage = lazy(() => import('./components/user/ProfilePage'));
const InviteUsersPage = lazy(() => import('./components/user/InviteUsersPage'));
const BillingPage = lazy(() => import('./components/user/BillingPage'));
const IntegrationsPage = lazy(() => import('./components/user/IntegrationsPage'));
const AuthPage = lazy(() => import('./components/user/AuthPage.jsx'));
const ShareableLinkPage = lazy(() => import('./components/user/ShareableLinkPage'));
const ShareableVideoPage = lazy(() => import('./components/user/ShareableVideoPage'));
const MascotAgentPage = lazy(() => import('./components/user/MascotAgentPage'));
const AcceptInvitePage = lazy(() => import('./components/user/AcceptInvitePage.jsx'));
const DomainJoinPage = lazy(() => import('./components/user/DomainJoinPage.jsx'));
const TermsOfService = lazy(() => import('./components/user/TermsOfService.jsx'));
const PrivacyPolicy = lazy(() => import('./components/user/PrivacyPolicy.jsx'));
const ElectronicSignaturesLegal = lazy(() => import('./components/user/ElectronicSignaturesLegal.jsx'));
const ChatInsightsView = lazy(() => import('./components/user/ChatInsightsView'));
const DocumentFillView = lazy(() => import('./components/user/DocumentFillView'));
const DocumentSignView = lazy(() => import('./components/user/DocumentSignView'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard.jsx'));
const ProductFeedbackPage = lazy(() => import('./components/user/ProductFeedbackPage.jsx'));
const AppDeepLinkFallback = lazy(() => import('./components/AppDeepLinkFallback.jsx'));
const SharedSignatureReceiptView = lazy(() => import('./components/user/SharedSignatureReceiptView'));
const PartnerConsolePage = lazy(() => import('./components/user/PartnerConsolePage.jsx'));
const BasedAIFab = lazy(() => import('./components/common/BasedAIFab'));
const CustomDomainRoot = lazy(() => import('./components/user/CustomDomainRoot.jsx'));

const MASCOT_LINK_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `mascot.example.com/{id}` renders the mascot page. Other hosts ignore this path. */
function MascotCustomHostLink() {
  const { kbId } = useParams();
  if (!isMascotCustomHostname() || !MASCOT_LINK_ID.test(kbId || '')) return null;
  return <MascotAgentPage />;
}

/** Old `mascot.example.com/mascot/{id}` links land on `/{id}`. trybased keeps `/mascot/{id}`. */
function MascotLegacyCustomHostRedirect() {
  const { kbId } = useParams();
  const location = useLocation();
  if (isMascotCustomHostname() && MASCOT_LINK_ID.test(kbId || '')) {
    return <Navigate to={`/${kbId}${location.search}${location.hash}`} replace />;
  }
  return <MascotAgentPage />;
}

function HostAwareHome() {
  if (typeof window !== 'undefined' && isCustomDomainHost()) {
    return <CustomDomainRoot />;
  }
  return <AuthenticatedHome />;
}

/** Staff app routes are Based-hosted only; send visitors on custom domains to trybased. */
function BasedHostOnlyOutlet() {
  useEffect(() => {
    if (!isCustomDomainHost()) return;
    const path = window.location.pathname + window.location.search + window.location.hash;
    window.location.replace(`https://trybased.com${path}`);
  }, []);
  if (isCustomDomainHost()) {
    return <FullScreenRouteSpinner />;
  }
  return <ClientSearchRouteGuard />;
}

if (typeof window !== 'undefined') {
  window.deriveKeyFromCredential = encryptionUtils.deriveKeyFromCredential;
  window.decryptText = encryptionUtils.decryptText;
}


// Custom hook for PromoteKit - moved outside component
export const usePromoteKit = () => {
  const [referralId, setReferralId] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const checkForReferralId = () => {
      // Skip if in development
      if (process.env.NODE_ENV === 'development') {
        devLog('PromoteKit: Using mock referral ID for development');
        setReferralId('dev-referral-123');
        setIsReady(true);
        return;
      }

      // Check if PromoteKit is loaded and referral ID is available
      if (window.promotekit_referral) {
        devLog('PromoteKit referral ID found:', window.promotekit_referral);
        setReferralId(window.promotekit_referral);
        setIsReady(true);
      } else {
        devLog('No PromoteKit referral ID found');
        setIsReady(true);
      }
    };

    // Wait a bit for PromoteKit to initialize
    const timer = setTimeout(checkForReferralId, 1000);

    return () => clearTimeout(timer);
  }, []);

  const trackReferral = (email, stripeCustomerId = null) => {
    if (process.env.NODE_ENV === 'development') {
      devLog('PromoteKit: Mock referral tracking for development', { email, stripeCustomerId });
      return true;
    }

    if (window.promotekit && window.promotekit.refer) {
      try {
        window.promotekit.refer(email, stripeCustomerId);
        devLog('PromoteKit referral tracked successfully');
        return true;
      } catch (error) {
        console.error('PromoteKit referral tracking error:', error);
        return false;
      }
    } else {
      console.warn('PromoteKit not available for referral tracking');
      return false;
    }
  };

  return {
    referralId,
    isReady,
    trackReferral,
    hasReferral: !!referralId
  };
};

function ChatInsightsPage() {
  const { workspaceId } = useParams();
  return (
    <div className="h-screen min-h-0">
      <ChatInsightsView workspaceId={workspaceId} />
    </div>
  );
}

function App() {
  const [, setPromoteKitStatus] = useState('loading');

  useEffect(() => {
    const loadPromoteKit = async () => {
      try {
        // Check if script already exists
        const existingScript = document.querySelector('script[data-promotekit]');
        if (existingScript) {
          devLog('PromoteKit script already exists');
          setPromoteKitStatus('loaded');
          return;
        }

        // Check if we're in development mode
        const isDevelopment = process.env.NODE_ENV === 'development' ||
          window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1';

        if (isDevelopment) {
          console.warn('PromoteKit: Skipping load in development environment');
          setPromoteKitStatus('dev');
          return;
        }

        devLog('Loading PromoteKit script...');

        const script = document.createElement('script');
        script.src = 'https://cdn.promotekit.com/promotekit.js';
        script.async = true;
        script.setAttribute('data-promotekit', 'ec511efb-9d39-4b69-8e75-da55116f39be');

        // Create a promise to handle the script loading
        const scriptLoadPromise = new Promise((resolve, reject) => {
          let isResolved = false;

          script.onload = () => {
            if (!isResolved) {
              isResolved = true;
              devLog('PromoteKit loaded successfully');
              setPromoteKitStatus('loaded');
              resolve();
              devLog('PromoteKit referral ID:', window.promotekit_referral);
              devLog('PromoteKit object:', window.promotekit);
            }
          };
          script.onerror = (error) => {
            if (!isResolved) {
              isResolved = true;
              console.error('Failed to load PromoteKit:', error);
              setPromoteKitStatus('error');
              reject(error);
            }
          };

          // Timeout after 10 seconds
          setTimeout(() => {
            if (!isResolved) {
              isResolved = true;
              console.error('PromoteKit load timeout');
              setPromoteKitStatus('error');
              reject(new Error('Script load timeout'));
            }
          }, 10000);
        });

        document.head.appendChild(script);
        await scriptLoadPromise;

      } catch (error) {
        console.error('Error loading PromoteKit:', error);
        setPromoteKitStatus('error');
      }
    };

    loadPromoteKit();
  }, []);

  return (
    <EncryptionKeyProvider>
      <ActiveWorkspaceProvider>
        <ClientSearchModeProvider>
        <Router>
          <div>
            <Suspense fallback={<FullScreenRouteSpinner />}>
              <Routes>
              <Route path="/home" element={<LandingPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/authpage" element={<AuthPage />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
              <Route path="/join/:clientSlug" element={<DomainJoinPage />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/electronic-signatures" element={<ElectronicSignaturesLegal />} />
              <Route path="/doc/:documentId/fill" element={<DocumentFillView />} />
              <Route path="/doc/:documentId" element={<DocPathRedirect />} />
              <Route path="/sign/:documentId/:envelopeId" element={<DocumentSignView />} />
              <Route path="/shared-signed/:documentId/:submissionId" element={<SharedSignatureReceiptView />} />
              <Route path="/shared/:kbId" element={<ShareableLinkPage />} />
              <Route path="/shared/video/:talkId" element={<ShareableVideoPage />} />
              <Route path="/mascot/:kbId" element={<MascotLegacyCustomHostRedirect />} />
              <Route path="/:kbId" element={<MascotCustomHostLink />} />

              <Route path="/" element={<HostAwareHome />} />

              <Route element={<BasedHostOnlyOutlet />}>
                <Route path="/invite" element={<InviteUsersPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/integrations" element={<IntegrationsPage />} />
                <Route path="/feedback" element={<ProductFeedbackPage />} />
                <Route path="/partner" element={<PartnerConsolePage />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/workspace/:workspaceId/chats" element={<ChatInsightsPage />} />
                <Route path="/app/chat/:chatId" element={<AppDeepLinkFallback />} />
              </Route>
            </Routes>
            </Suspense>

            <Suspense fallback={null}>
              <BasedAIFab />
            </Suspense>

            {/* Toast container — global settings */}
            <ToastContainer
              position="bottom-right" // change to your desired default
              autoClose={3500}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />

            {/* Global Toaster for react-hot-toast */}
            <Toaster position="bottom-right" />

            <SpeedInsights />
          </div>
        </Router>
        </ClientSearchModeProvider>
      </ActiveWorkspaceProvider>
    </EncryptionKeyProvider>
  );
}

export { devLog } from './utils/devLog';

// Mobile debug logging - always logs, even in production (for remote debugging)
// Use this for critical mobile issues that need debugging on real devices
export const mobileLog = (...args) => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const timestamp = new Date().toISOString();
  const logPrefix = `[MOBILE-${timestamp}]`;

  // Always log to console (visible in remote debugging)
  console.log(logPrefix, ...args);

  // Also store in window for easy access
  if (!window.mobileLogs) {
    window.mobileLogs = [];
  }
  window.mobileLogs.push({
    timestamp,
    isMobile,
    args: args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg))
  });

  // Keep only last 100 logs to prevent memory issues
  if (window.mobileLogs.length > 100) {
    window.mobileLogs.shift();
  }
};

export default App;
